import { useState, useEffect } from 'react';
import { Stack, NumberInput, Button, Text, Switch, Group, Paper, Title, SimpleGrid, Divider, Radio } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconCalculator } from '@tabler/icons-react';
import { MONTH_NAMES_PL } from '../../../../types/calculator';
import { HourlyConsumptionConfig } from './HourlyConsumptionConfig';
import type { CalculatorFormValues, HourlyConfig, TariffType } from '../../../../types/calculator';
// OSD Metadata for dynamic messages
import { getOSDShortName, getAvailableTariffsForOSD, type OSDCode } from '../../../../utils/osdMetadata';

/**
 * Calculator Form Component Props
 */
interface CalculatorFormProps {
  onSubmit: (values: CalculatorFormValues) => void;
  loading?: boolean;
  initialValues?: CalculatorFormValues;
  /** Selected OSD for dynamic messages */
  selectedOSD?: OSDCode;
  /** Calculation year for dynamic messages */
  calculationYear?: number;
}

/**
 * CalculatorForm Component
 *
 * Updated: Support for 6 energy tariffs with statistical data and consumption patterns
 * - Annual consumption (kWh/year)
 * - Current tariff (G11, G12, G12as, G12w, G13, G13s)
 * - Household size and characteristics (heat pump, solar, EV)
 * - Consumption pattern selection
 *
 * @param onSubmit - Callback when form is submitted
 * @param loading - Loading state for calculate button
 */
export function CalculatorForm({
  onSubmit,
  loading = false,
  initialValues,
  selectedOSD = 'TAURON',
  calculationYear = 2026,
}: CalculatorFormProps) {
  // Polish month names
  const MONTHS = Object.values(MONTH_NAMES_PL);

  // State for monthly consumption
  const [monthlyConsumption, setMonthlyConsumption] = useState<number[]>(
    initialValues?.monthlyConsumption || Array(12).fill(292) // Default: 3500/12 ≈ 292 kWh/month
  );

  // State for annual consumption helper (optional quick-fill)
  const [annualValue, setAnnualValue] = useState<number>(
    initialValues?.monthlyConsumption
      ? initialValues.monthlyConsumption.reduce((sum, val) => sum + val, 0)
      : 3500
  );

  // Helper: Migrate old decimal values (0-1) to percentage values (0-100)
  const migrateHourlyConfig = (config?: HourlyConfig): HourlyConfig => {
    if (!config) {
      return {
        weekday: Array(24).fill(100 / 24), // Uniform by default (4.166% per hour)
        weekend: Array(24).fill(100 / 24),
      };
    }

    // Check if values are in old decimal format (all values < 2 indicates decimal format)
    const isDecimalFormat = config.weekday.every(v => v < 2) && config.weekend.every(v => v < 2);

    if (isDecimalFormat) {
      // Convert from decimal (0-1) to percentage (0-100)
      return {
        weekday: config.weekday.map(v => v * 100),
        weekend: config.weekend.map(v => v * 100),
      };
    }

    // Already in percentage format
    return config;
  };

  // State for hourly configuration with migration
  const [hourlyConfig, setHourlyConfig] = useState<HourlyConfig>(
    migrateHourlyConfig(initialValues?.hourlyConfig)
  );

  // Helper: Copy annual value to all months
  const copyToAllMonths = () => {
    const monthlyValue = Math.round(annualValue / 12);
    setMonthlyConsumption(Array(12).fill(monthlyValue));
  };

  // Initialize form with Mantine useForm hook
  const form = useForm<CalculatorFormValues>({
    initialValues: initialValues || {
      monthlyConsumption: Array(12).fill(292), // Default: ~292 kWh/month
      currentTariff: 'G11',                    // Default: G11 (always)
      householdSize: 2,                        // Default: 2 people
      hasHeatPump: false,                      // Default: no heat pump
      hasSolar: false,                         // Default: no solar panels
      hasEV: false,                            // Default: no electric vehicle
      consumptionPattern: 'UNIFORM',           // Default: uniform consumption
      hourlyConfig: {
        weekday: Array(24).fill(100 / 24), // 4.166% per hour
        weekend: Array(24).fill(100 / 24),
      }
    },

    // Validation rules
    validate: {
      monthlyConsumption: (values) => {
        if (!values || values.length !== 12) {
          return 'Wymagane jest 12 miesięcznych wartości';
        }
        if (values.some(v => v < 0)) {
          return 'Miesięczne zużycie nie może być ujemne';
        }
        if (values.some(v => v > 5000)) {
          return 'Miesięczne zużycie nie może przekraczać 5000 kWh';
        }
        return null;
      },
      householdSize: (value) => {
        if (value !== '5+' && (value < 1 || value > 5)) {
          return 'Nieprawidłowa liczba osób';
        }
        return null;
      }
    }
  });

  // CRITICAL: Update form values when initialValues changes (pre-fill from saved calculation)
  useEffect(() => {
    if (initialValues) {
      form.setValues(initialValues);
      setMonthlyConsumption(initialValues.monthlyConsumption || Array(12).fill(292));
      setHourlyConfig(migrateHourlyConfig(initialValues.hourlyConfig)); // Use migration function

      // Update annual value
      if (initialValues.monthlyConsumption) {
        const total = initialValues.monthlyConsumption.reduce((sum, val) => sum + val, 0);
        setAnnualValue(total);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- form intentionally excluded to prevent infinite updates
  }, [initialValues]);

  // Handle form submission
  const handleSubmit = (values: CalculatorFormValues) => {
    // Update monthly consumption from state
    values.monthlyConsumption = monthlyConsumption;
    values.hourlyConfig = hourlyConfig;
    onSubmit(values);
  };

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="xl">
        {/* Basic Consumption Data */}
        <Paper p="md" withBorder>
          <Stack gap="md">
            <div>
              <Title order={4}>Krok 1: Podstawowe dane</Title>
              <Text size="sm" c="dimmed" mt={4}>
                Wybierz swoją obecną taryfę i wprowadź miesięczne zużycie energii. Kalkulator automatycznie porówna koszty dla wszystkich 6 dostępnych taryf.
              </Text>
            </div>

            {/* Current Tariff Selection */}
            <Radio.Group
              label="Jaka jest Twoja obecna taryfa?"
              description="Wybierz taryfę, którą aktualnie posiadasz w swoim kontrakcie z dostawcą energii"
              value={form.values.currentTariff}
              onChange={(value) => form.setFieldValue('currentTariff', value as TariffType)}
              withAsterisk
            >
              <Group mt="xs">
                <Radio value="G11" label="G11 (jednostrefowa)" />
                <Radio value="G12" label="G12 (dwustrefowa)" />
                <Radio value="G12as" label="G12as (agroenergetyczna)" />
                <Radio value="G12w" label="G12w (weekendowa)" />
                <Radio value="G13" label="G13 (trzystrefowa)" />
                <Radio value="G13s" label="G13s (sobotnia)" />
              </Group>
            </Radio.Group>

            <Divider my="xs" label="Miesięczne zużycie energii" labelPosition="center" />

            {/* Quick annual consumption helper */}
            <Group justify="space-between" align="flex-end">
              <NumberInput
                label="Roczne zużycie (opcjonalnie)"
                description="Wpisz roczne zużycie, a następnie kliknij przycisk aby skopiować do wszystkich miesięcy"
                placeholder="3500"
                value={annualValue}
                onChange={(value) => setAnnualValue(Number(value))}
                suffix=" kWh/rok"
                min={100}
                max={50000}
                step={100}
                size="md"
                style={{ flex: 1 }}
              />
              <Button onClick={copyToAllMonths} variant="light" size="md">
                Skopiuj do wszystkich miesięcy
              </Button>
            </Group>

            <Divider label="lub wprowadź dla każdego miesiąca osobno" labelPosition="center" />

            {/* Monthly consumption inputs - 3 columns per row */}
            <SimpleGrid cols={{ base: 2, sm: 3, md: 3 }} spacing="sm">
              {MONTHS.map((month, index) => (
                <NumberInput
                  key={month}
                  label={month}
                  placeholder="292"
                  value={monthlyConsumption[index]}
                  onChange={(value) => {
                    const newValues = [...monthlyConsumption];
                    newValues[index] = Number(value);
                    setMonthlyConsumption(newValues);
                  }}
                  suffix=" kWh"
                  min={0}
                  max={5000}
                  size="sm"
                />
              ))}
            </SimpleGrid>

            {/* Annual sum display */}
            <Text size="sm" c="dimmed">
              Suma roczna: {monthlyConsumption.reduce((a, b) => a + b, 0).toLocaleString('pl-PL')} kWh
            </Text>
          </Stack>
        </Paper>

        {/* Statistical Data (Krok 2) */}
        <Paper p="md" withBorder>
          <Stack gap="md">
            <div>
              <Title order={4}>Krok 2: Informacje statystyczne</Title>
              <Text size="sm" c="dimmed" mt={4}>
                Te dane są potrzebne do dokładniejszej kalkulacji sposobu korzystania z energii.
                Pomogą nam przewidzieć, w jakich godzinach zużywasz najwięcej energii.
              </Text>
            </div>

            {/* Household Size - Radio Selector */}
            <Radio.Group
              label="Liczba osób w gospodarstwie domowym"
              description="Wybierz liczbę osób mieszkających w gospodarstwie"
              value={form.values.householdSize.toString()}
              onChange={(value) => form.setFieldValue(
                'householdSize',
                value === '5+' ? '5+' : Number(value)
              )}
              required
            >
              <Group mt="xs">
                <Radio value="1" label="1 osoba" />
                <Radio value="2" label="2 osoby" />
                <Radio value="3" label="3 osoby" />
                <Radio value="4" label="4 osoby" />
                <Radio value="5+" label="5 i więcej" />
              </Group>
            </Radio.Group>

            {/* Heat Pump Switch */}
            <Switch
              label="Pompa ciepła"
              description="Czy posiadasz pompę ciepła?"
              size="md"
              {...form.getInputProps('hasHeatPump', { type: 'checkbox' })}
            />

            {/* Solar Panels Switch */}
            <Switch
              label="Fotowoltaika"
              description="Czy posiadasz instalację fotowoltaiczną?"
              size="md"
              {...form.getInputProps('hasSolar', { type: 'checkbox' })}
            />

            {/* Electric Vehicle Switch */}
            <Switch
              label="Samochód elektryczny"
              description="Czy posiadasz samochód elektryczny?"
              size="md"
              {...form.getInputProps('hasEV', { type: 'checkbox' })}
            />
          </Stack>
        </Paper>

        {/* Hourly Consumption Configuration (now Krok 3) */}
        <HourlyConsumptionConfig
          value={hourlyConfig}
          onChange={setHourlyConfig}
          householdSize={form.values.householdSize}
          hasHeatPump={form.values.hasHeatPump}
          hasSolar={form.values.hasSolar}
          hasEV={form.values.hasEV}
          monthlyConsumption={monthlyConsumption}
        />

        {/* Calculate Button */}
        <Button
          type="submit"
          size="lg"
          leftSection={<IconCalculator size={20} />}
          loading={loading}
          mt="md"
          fullWidth
        >
          {loading ? 'Obliczanie...' : 'Oblicz i porównaj wszystkie taryfy'}
        </Button>

        {/* Helper Text - Dynamic based on selected OSD */}
        <Text size="sm" c="dimmed" ta="center">
          Kalkulator automatycznie porówna koszty dla {getAvailableTariffsForOSD(selectedOSD).length} najpopularniejszych taryf dostępnych w {getOSDShortName(selectedOSD)} w {calculationYear} roku.
        </Text>
      </Stack>
    </form>
  );
}
